import type { DecisionSnapshot, OrderIntent } from '../types/snapshot'
import { F, resolveFacts, type FactSet } from './facts'
import type { CompareOp, RulePredicate, RuleSpec, Verdict, Violation } from './types'

/**
 * Tier 1: the instant, local verdict.
 *
 * Mirror of Guardian.Core/PreTradeEvaluator.cs. Advisory only — the server re-evaluates
 * on submit and this verdict is never transmitted, so there is nothing here for a
 * tampered client to forge. Its job is purely to make the ticket feel immediate.
 *
 * Both interpreters read the same spec from the same facts, so they must always agree.
 * A disagreement is a bug in one of them and raises the drift alarm.
 */
export function evaluate(
  spec: readonly RuleSpec[],
  snapshot: DecisionSnapshot,
  intent: OrderIntent,
  nowMs: number,
): Verdict {
  const facts = resolveFacts(snapshot, intent, nowMs)
  const violations: Violation[] = []

  for (const rule of spec) {
    if (!matches(rule.when, facts)) continue

    const [fact, limit] = explain(rule.when)
    violations.push({
      rule: rule.id,
      mode: rule.mode,
      message: rule.message,
      fact,
      actual: facts.values[fact] ?? 0,
      limit,
    })
  }

  return {
    violations,
    riskPercent: facts.riskPercent,
    estimatedMargin: facts.estimatedMargin,
    notional: facts.notional,
    needsPeerApproval: read(facts, F.approvalRequired) === 1,
    isBlocked: violations.some((v) => v.mode === 'Enforce'),
  }
}

/**
 * An unknown fact throws rather than resolving to "no violation" — silently disabling a
 * rule because of a typo is the worst possible failure here. Matches the C# behaviour.
 */
function read(facts: FactSet, fact: string): number {
  const value = facts.values[fact]
  if (value === undefined) {
    throw new Error(`Unknown fact '${fact}'. Add it to facts.ts and to Facts.Resolve.`)
  }
  return value
}

export function matches(p: RulePredicate, facts: FactSet): boolean {
  switch (p.kind) {
    case 'IsTrue':
      return read(facts, p.fact) === 1
    case 'Compare':
      return apply(p.op, read(facts, p.fact), p.value)
    case 'Negate':
      return !matches(p.of, facts)
    case 'AllOf':
      return p.of.length > 0 && p.of.every((x) => matches(x, facts))
    case 'AnyOf':
      return p.of.some((x) => matches(x, facts))
  }
}

function apply(op: CompareOp, actual: number, value: number): boolean {
  switch (op) {
    case 'Lt':
      return actual < value
    case 'Lte':
      return actual <= value
    case 'Gt':
      return actual > value
    case 'Gte':
      return actual >= value
    case 'Eq':
      return actual === value
    case 'Neq':
      return actual !== value
  }
}

/** First leaf of the predicate tree — enough to answer "which number tripped this?". */
function explain(p: RulePredicate): [string, number] {
  switch (p.kind) {
    case 'Compare':
      return [p.fact, p.value]
    case 'IsTrue':
      return [p.fact, 1]
    case 'Negate':
      return explain(p.of)
    case 'AllOf':
    case 'AnyOf':
      return p.of.length > 0 ? explain(p.of[0]!) : ['(composite)', 0]
  }
}
